

exports.__esModule = true;

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

/*
 Class
 -----

 A class pattern, adding sugar on top of javascript prototypes

 ### Basic Usage

 let MyClass = Class.extend({
 foo: 'bar';
 });

 myInstance = new MyClass({
 hello: 'world'
 });

 console.log(myInstance.foo, myInstance.hello)

 In this example, `foo` becomes a property on `MyClass`'s prototype,
 and `hello` becomes a property on `myInstance`.

 ### Default Object Methods

 - `init()`: Called after the object is first instantiated, with all properties available

 - `get(<path>, <default>)`: Get an object an arbitrary number of levels deep, or return a default

 e.g. `myInstance.get('foo.bar.baz', 'some_default');`

 Default return value is undefined

 - `set(<path>, <value>)`: Set an object an arbitrary number of levels deep

 e.g. `myInstance.set('foo.bar.baz', 'some_value');`

 Error is thrown if no object is found to set the value on

 - `setProperties(<object>)`: Set all the properties from an object onto your object

 e.g. `myInstance.setProperties({foo: 'bar', hello: 'world'})`

 Can not be used with nested properties

 - `forEach(<callback>)`: Loop through the object and call callback(<key>, <value>) for each key/val pair

 */

// Object Create
// -------------
//
// Shim for Object.create, if it is not supported by our browser
//
// let myPrototype = {foo: 'bar'}
// let myObject = create(myPrototype)

// Create an empty anonymous constructor with no side effects, for re-use later
var EmptyConstructor = function EmptyConstructor() {}; // eslint-disable-line no-empty-function

var create = Object.create || function create(obj) {

    // Set a temporary prototype
    EmptyConstructor.prototype = obj;

    // Create an object using our temporary constructor/prototype
    var instance = new EmptyConstructor();

    // Reset the prototype for next time
    EmptyConstructor.prototype = null;

    // Return our new object, created without side effects
    return instance;
};

function _extend(obj, source) {

    if (!source) {
        return obj;
    }

    for (var key in source) {
        if (source.hasOwnProperty(key)) {
            obj[key] = source[key];
        }
    }

    return obj;
}

// Is Object
// ---------
//
// Check of a let is an object or not
//
// isobject({}) -> true
// isobject('foo') -> false

function isobject(obj) {
    return obj && (typeof obj === 'undefined' ? 'undefined' : _typeof(obj)) === 'object' && obj instanceof Object;
}

// Transpose properties
// -------------------------------------------------------------------
//
// Transpose properties from a list of objects onto a recipient object
//
// let recipient = {};
// transpose(recipient, [{a: 1, b: 2}, {c: 3}])
// recipient -> {a: 1, b: 2, c:3}

function transpose(recipient, args) {

    // Loop through all args
    for (var i = 0; i < args.length; i++) {
        var ob = args[i];

        // Ignore anything that is not an object
        if (!isobject(ob)) {
            continue;
        }

        // Loop through all keys in our object
        for (var key in ob) {

            // Ignore any prototypal properties
            if (!ob.hasOwnProperty(key)) {
                continue;
            }

            var item = ob[key];

            // Give the function a name
            if (item instanceof Function) {
                item.__name__ = key;
            }

            recipient[key] = item;
        }
    }
}

// Construct a Class
// -----------------
//
// This is the primary constructor for all $Class objects

function construct() {
    // eslint-disable-line no-unused-vars

    // Call any custom constructor;
    if (this.construct) {
        var ob = this.construct.apply(this, arguments);

        // If we get a new object back, return that to the user
        if (isobject(ob)) {
            return ob;
        }
    }

    // Load any provided properties directly onto the object
    transpose(this, arguments);

    // Initialize the object
    if (this.init) {
        this.init();
    }
}

// Reopen an object
// ----------------
//
// Add properties to the constructor's prototype
//
//     let Animal = $Class.extend();
//
//     Animal.reopen({
//       type: 'animal',
//     });
//
//     let cat = new Animal;
//     console.log(cat.type);
function reopen() {

    // Add some more properies to our prototype
    transpose(this.prototype, arguments);

    return this;
}

// Reopen a class
// --------------
//
// Add properties to the constructor. These will propogate through future extends
//
//     let Animal = $Class.extend();
//
//     Animal.reopenClass({
//       fromCache: function(id) {
//         return cache[id] || new this({id: id});
//       }
//     }
//
//     let cat = Animal.fromCache('cat_54');

function reopenClass() {

    // Add some more properies to our class
    transpose(this, arguments);

    // Add to __classmethods__
    transpose(this.__classmethods__, arguments);

    return this;
}

function isChild(Cls) {
    return Cls && Cls.prototype instanceof this;
}

// Generate a new class
// --------------------
//
// Generate a new constructor, with the specified name, and attach all static and prototypal methods

var id = 0;

function extend(name) {
    var Cls = void 0;
    var className = void 0;
    var args = void 0;
    var argsLength = void 0;
    var startIndex = void 0;

    // Accept an optional class name
    if (typeof name === 'string') {

        // Guard against javascript injection
        if (!name.match(/^[\w$][\w\d]*$/)) {
            throw new Error('Class name can not include special characters: ' + name);
        }

        // Use the provided name and slice out the first argument
        className = name;
        argsLength = arguments.length && arguments.length - 1;
        startIndex = 1;
    } else {

        // Otherwise default to the parent name, or 'Object' if generating our first class
        className = this.name || 'Object';
        argsLength = arguments.length;
        startIndex = 0;
    }

    args = new Array(argsLength);
    for (var i = startIndex; i < arguments.length; i++) {
        args[i - startIndex] = arguments[i];
    }

    // Dynamically create our constructor with a custom name
    eval('Cls = function ' + className + '() { return construct.apply(this, arguments) }'); // eslint-disable-line no-eval

    // Store the class name for future reference
    Cls.__name__ = className;

    // Set up a classmethods object. These will be inherited through subsequent extend() calls
    Cls.__classmethods__ = {
        extend: extend,
        reopen: reopen,
        reopenClass: reopenClass,
        isChild: isChild
    };

    id += 1;
    Cls.id = id;

    // If we're being called on an existing class, e.g. Class.extend(), we should inherit from that class
    if (this && this !== window) {

        // Inherit everything from superclass prototype
        Cls.prototype = create(this.prototype);

        // Re-set the constructor, which is overwritten by create
        Cls.prototype.constructor = Cls;

        // Add a reference to the superclass prototype for super method calls
        Cls.prototype._super = this.prototype;

        // Inherit any class methods from the superclass
        _extend(Cls.__classmethods__, this.__classmethods__);
    }

    // Add the class methods to our existing class
    _extend(Cls, Cls.__classmethods__);

    // Load any provided properties onto the constructor's prototype
    transpose(Cls.prototype, args);

    // Temporary: allow access from console/debugger
    if (className.indexOf('$') === 0) {
        window[className] = Cls;
    } else {
        window['$' + className] = Cls;
    }

    return Cls;
}

// Multi-layer get
// ---------------
//
// Recursively gets a deep path from an object, returning a default value if any level is not found
//
// let obj = {bar: {baz: 1}}
// get(obj, 'bar.baz', 'default') -> 1
// get(obj, 'aaa.bbb', 'default') -> default

function _get(item, path, def) {

    if (!path) {
        return def;
    }

    path = path.split('.');

    // Loop through each section of our key path
    for (var i = 0; i < path.length; i++) {

        // If we have an object, we can get the key
        if (isobject(item)) {
            item = item[path[i]];
            // Otherwise, we should return the default (undefined if not provided)
        } else {
            return def;
        }
    }

    // If our final result is undefined, we should return the default
    return item === undefined ? def : item;
}

// Multi-layer set
// ---------------
//
// Recursively sets a deep path from an object
//
// let obj = {bar: {baz: 1}}
// set(obj, 'bar.baz', 2)
// obj.bar.baz -> 1

function _set(item, path, value) {
    path = path.split('.');

    // Loop through each section of our key path except the last
    for (var i = 0; i < path.length - 1; i++) {

        // Get the next item down
        item = item[path[i]];

        // If we have an object, we're good
        if (isobject(item)) {
            continue;
            // Otherwise, there's nothing to set our key on
        } else {
            throw new Error(path[i - 1] + '.' + path[i] + ' is not an object');
        }
    }

    // Do the set on the retrieved object with our value
    item[path[path.length - 1]] = value;
}

// Loop through object
// -------------------
//
// Loop an object and apply a function for each key/value
//
// let ob = {a: 1, b: 2, c: 3}
// each(ob, console.log) -> a 1  b 2  c 3

function each(ob, callback) {
    for (var key in ob) {
        if (ob.hasOwnProperty(key)) {
            callback.call(ob, key, ob[key]);
        }
    }
}

// Gey keys of object
// ------------------
//
// Shim for Object.keys if it does not already exist
//
// let ob = {a: 1, b: 2, c: 3}
// keys(ob) -> ['a', 'b', 'c']

function _keys(ob) {

    if (Object.keys) {
        return Object.keys(ob);
    }

    var result = [];

    for (var key in ob) {
        if (ob.hasOwnProperty(key)) {
            result.push(key);
        }
    }

    return result;
}

// Create a base class that can be inherited from
// ----------------------------------------------
//
// This is the public interface to the Class module.
// All classes with extend from Class and be provided with a set of base convenience methods.

var $Class = exports.$Class = extend('Class', {

    init: function init() {
        // Empty init class, so calls to _super can be safely made
    },

    get: function get(path, def) {
        return _get(this, path, def);
    },

    set: function set(path, value) {
        _set(this, path, value);
    },

    setProperties: function setProperties() {
        transpose(this, arguments);
    },

    forEach: function forEach(callback) {
        each(this, callback);
    },

    keys: function keys() {
        return _keys(this);
    }
});

$Class.get = _get;
$Class.set = _set;
$Class.keys = _keys;

angular.value("$Class", $Class);


//////////////////
// WEBPACK FOOTER
// ./components/squid/dist/core/class.js
// module id = ./components/squid/dist/core/class.js
// module chunks = 1